import { flagEnabled } from "../../config";
import { useGstWording } from "../plans/useGstWording";
import { pinWords } from "../../api/planFamily";
import { useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { checkLogin, formLogins, inUseMessage } from "../../api/loginCheck";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useIdentity } from "../../auth/useIdentity";
import { CashCodeDialog } from "../../components/CashCodeDialog";
import { canCreateSubscriber, RECHARGE_PATH } from "../../components/nav";
import {
  receiptKindLabel,
  saleReceipts,
  type RechargeMethod,
  type RechargePlan,
} from "../../api/subscriberRecharge";
import { followAddresses, rupees, type CartQuote } from "../../api/cart";
import { CartAddons, useCartQuote } from "./CartAddons";
import { PayOnlineNotice, UpiPayQR, usePayOptions } from "../payments/PayBlock";
import { PAY_ONLINE_OFF_NOTICE, type PayOptions } from "../../api/payOptions";
import { PlanPicker, usePlanTermPrices, type TermPrices } from "./PlanPicker";
import {
  AddressFields,
  ExtraLoginsNote,
  LoginInstallationFields,
} from "./InstallationFields";
import {
  createBody,
  createFailure,
  createFieldErrors,
  createFormErrors,
  createLogins,
  installAddresses,
  postCreateSubscriber,
  installationsCompletedMessage,
  isDeviceOnly,
  isInstallationsOnly,
  listCreatePlans,
  loginDetailsLine,
  whyNotCreatable,
  EMPTY_CREATE_FORM,
  type CreateCart,
  type CreatedSubscriber,
  type CreateSubscriberForm,
  type CreateSubscriberInput,
  type Gender,
} from "../../api/subscriberCreate";

/** The ID proof types the PHP form offered (create_user.php). */
export const ID_PROOF_TYPES = [
  "Aadhaar",
  "PAN",
  "Voter ID",
  "Driving licence",
  "Passport",
  "Other",
];

/**
 * Users › Create subscriber (owner 2026-10-14): one form, one call. The
 * service registers the subscriber, applies the plan (one pin, if the plan
 * uses pins) and generates the password, which is shown here once.
 */
export function CreateUser() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  // Owner 2026-10-29: no word of GST for an unregistered or composition operator.
  const { speak: speakGst } = useGstWording(ns);
  const allowed = canCreateSubscriber(identity.roles);
  const [form, setForm] = useState<CreateSubscriberForm>(EMPTY_CREATE_FORM);
  const payOptions = usePayOptions(identity.namespace ?? "");
  const [touched, setTouched] = useState(false);

  const plans = useQuery({
    queryKey: ["createplans", ns],
    enabled: Boolean(identity.token && ns && allowed),
    queryFn: ({ signal }) => listCreatePlans(identity, ns, signal),
    retry: false,
  });

  // Owner PC-1/PC-2: installation (and a router sold outright) once per address.
  const addresses = installAddresses(form);
  const cart = useCartQuote(
    identity,
    ns,
    "create",
    form.plan,
    form.addons,
    undefined,
    addresses,
  );
  const changeForm = (next: CreateSubscriberForm) =>
    setForm({
      ...next,
      addons: followAddresses(
        next.addons,
        cart.data?.offered ?? [],
        addresses,
        installAddresses(next),
      ),
    });
  const termPrices = usePlanTermPrices(
    identity,
    ns,
    "create",
    undefined,
    plans.data ?? [],
    form.plan,
    (pl) => whyNotCreatable(pl),
  );
  const cartState: CreateCart = {
    total: cart.data ? cart.data.total : null,
    pending: cart.pending,
    wired: Boolean(cart.data?.wired),
    extraDevices: cart.data?.extradevices ?? 0,
  };

  // One login, one subscriber (owner 2026-10-18): ask as each mobile or MAC
  // is complete; the server refuses at Create regardless.
  const logins = formLogins(form);
  const loginChecks = useQueries({
    queries: logins.map((l) => ({
      queryKey: ["logincheck", ns, l.value.toLowerCase()],
      enabled: Boolean(identity.token && ns && allowed),
      staleTime: 30_000,
      retry: false,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        checkLogin(identity, ns, l.value, signal),
    })),
  });
  const inUse: Record<string, string> = {};
  logins.forEach((l, i) => {
    const msg = loginChecks[i]?.data
      ? inUseMessage(l.field, loginChecks[i].data)
      : "";
    if (msg && !(l.key in inUse)) inUse[l.key] = msg;
  });

  // The total the operator saw when pressing Create: the success card shows it
  // when the server's answer does not carry one.
  const [submittedTotal, setSubmittedTotal] = useState<number | null>(null);

  // The body last sent: a retry after a 424 resends exactly this.
  const [sentBody, setSentBody] = useState<CreateSubscriberInput | null>(null);
  // A cash sale waits here for its cash code (components/CashCodeDialog.tsx).
  const [cashFor, setCashFor] = useState<CreateSubscriberInput | null>(null);
  const create = useMutation<CreatedSubscriber, Error, CreateSubscriberInput>({
    mutationFn: (body) => postCreateSubscriber(identity, ns, body),
  });

  const reset = () => {
    setForm(EMPTY_CREATE_FORM);
    setTouched(false);
    setSubmittedTotal(null);
    setSentBody(null);
    create.reset();
  };

  return (
    <>
      <CreateSubscriberView
        payOptions={payOptions.data}
        payOptionsFailed={payOptions.isError}
        payOnlineOn={flagEnabled("recharge.payonline")}
        ns={ns}
        allowed={allowed}
        roles={identity.roles}
        form={form}
        onChange={changeForm}
        touched={touched}
        plans={plans.data ?? null}
        plansError={plans.error ? plans.error.message : null}
        termPrices={termPrices}
        pending={create.isPending}
        error={create.error}
        created={create.data ?? null}
        quote={cart.data}
        quoteLoading={cart.pending}
        speakGst={speakGst}
        quoteError={form.plan ? cart.error : null}
        recordedTotal={create.data?.total ?? submittedTotal}
        sentBody={sentBody}
        inUse={inUse}
        onSubmit={() => {
          setTouched(true);
          if (
            createFormErrors(form, cartState).length === 0 &&
            Object.keys(inUse).length === 0
          ) {
            const body = createBody(form, cartState.total, cartState.wired);
            if (body.payment?.method === "cash") {
              setCashFor(body);
              return;
            }
            setSubmittedTotal(cartState.total);
            setSentBody(body);
            create.mutate(body);
          }
        }}
        onRetry={() => {
          if (sentBody) create.mutate(sentBody);
        }}
        onReset={reset}
      />
      {cashFor && (
        <CashCodeDialog
          open
          identity={identity}
          ns={ns}
          account=""
          amount={cartState.total ?? 0}
          saleRef={cashFor.mobile}
          onCancel={() => setCashFor(null)}
          onConfirm={(code) => {
            const body =
              code && cashFor.payment
                ? {
                    ...cashFor,
                    payment: { ...cashFor.payment, cash_code: code },
                  }
                : cashFor;
            setCashFor(null);
            setSubmittedTotal(cartState.total);
            setSentBody(body);
            create.mutate(body);
          }}
        />
      )}
    </>
  );
}

export interface CreateSubscriberViewProps {
  ns: string;
  allowed: boolean;
  roles: string[];
  form: CreateSubscriberForm;
  onChange: (f: CreateSubscriberForm) => void;
  /** How a payment can be taken here; absent until read. */
  payOptions?: PayOptions | undefined;
  payOptionsFailed?: boolean | undefined;
  /** recharge.payonline: read by the parent so this view stays a pure render. */
  payOnlineOn?: boolean | undefined;
  /** The operator pressed Create at least once: show what is wrong. */
  touched: boolean;
  /** null while loading or when the load failed. */
  plans: RechargePlan[] | null;
  plansError: string | null;
  /** Each duration's server price (usePlanTermPrices); absent shows durations without prices. */
  termPrices?: TermPrices;
  pending: boolean;
  error: Error | null;
  created: CreatedSubscriber | null;
  /** The server's quote for the chosen plan and add-ons; null without one. */
  quote?: CartQuote | null;
  quoteLoading?: boolean;
  /** false: an unregistered or composition operator -- no word of GST. */
  speakGst?: boolean;
  quoteError?: string | null;
  /** The cart total recorded by the create, for the success card. */
  recordedTotal?: number | null;
  /** The body last sent; names the logins a 424 left without an installation. */
  sentBody?: CreateSubscriberInput | null;
  /** Logins of this form already in use on the site, by field key (createFieldErrors'); Create waits until there are none. */
  inUse?: Record<string, string>;
  onSubmit: () => void;
  /** Resend the same body (after a 424 whose installations did not all land). */
  onRetry?: () => void;
  onReset: () => void;
  now?: Date;
}

/** The screen without its data hooks, so a test can render it. */
export function CreateSubscriberView(p: CreateSubscriberViewProps) {
  const { form } = p;
  const set = <K extends keyof CreateSubscriberForm>(
    k: K,
    v: CreateSubscriberForm[K],
  ) => p.onChange({ ...form, [k]: v });
  const quote = p.quote ?? null;
  const wired = Boolean(form.plan && quote?.wired);
  const cart: CreateCart = {
    total: quote ? quote.total : null,
    pending: Boolean(p.quoteLoading),
    wired,
    extraDevices: wired ? (quote?.extradevices ?? 0) : 0,
  };
  const problems = createFormErrors(form, cart);
  // Owner 2026-10-18: errors on the field itself, in red, as soon as they are
  // known -- not in a list at the foot of the page.
  const inUse = p.inUse ?? {};
  const fe: Record<string, string> = {
    ...createFieldErrors(form, cart, p.touched),
    ...inUse,
  };
  const err = (k: string) => (fe[k] ? { error: true, helperText: fe[k] } : {});
  const quoteTotal = quote?.total ?? null;
  const needsPayment = quoteTotal != null && quoteTotal > 0;
  const failure = p.error ? createFailure(p.error, p.sentBody) : null;

  let body: ReactNode = null;
  if (p.created) {
    body = (
      <CreatedCard
        created={p.created}
        email={p.sentBody?.email ?? ""}
        total={p.recordedTotal ?? null}
        onReset={p.onReset}
      />
    );
  } else if (p.allowed) {
    body = (
      <>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                size="small"
                label="Mobile"
                value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
                slotProps={{
                  htmlInput: { inputMode: "numeric", maxLength: 10 },
                }}
                helperText="10 digits — the subscriber's login"
                {...err("mobile")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                required
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                {...err("name")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Gender"
                value={form.gender}
                onChange={(e) => set("gender", e.target.value as Gender)}
                {...err("gender")}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PlanPicker
                plans={p.plans ?? []}
                termPrices={p.termPrices ?? {}}
                value={form.plan}
                onChange={(g) => p.onChange({ ...form, plan: g, addons: {} })}
                why={(pl) => whyNotCreatable(pl, p.now)}
                tag={pinWords}
                disabled={!p.plans?.length}
                error={Boolean(p.plansError || fe.plan)}
                helperText={
                  p.plansError
                    ? `Plans could not be loaded: ${p.plansError}`
                    : p.plans == null
                      ? "Loading plans…"
                      : !p.plans.length
                        ? "No plans in this namespace"
                        : fe.plan || " "
                }
              />
            </Grid>
            {form.plan && (
              <Grid size={{ xs: 12 }}>
                <CartAddons
                  mode="create"
                  speakGst={p.speakGst ?? true}
                  quote={quote}
                  loading={Boolean(p.quoteLoading)}
                  error={p.quoteError ?? null}
                  selection={form.addons}
                  onChange={(addons) => set("addons", addons)}
                  disabled={p.pending}
                  pinLabel={pinWords(
                    p.plans?.find((x) => x.groupname === form.plan),
                  )}
                  addresses={installAddresses(form)}
                />
              </Grid>
            )}
            {needsPayment && (
              <Grid size={{ xs: 12 }}>
                <FormLabel required error={Boolean(fe.paymethod)}>
                  Payment method{fe.paymethod ? ` — ${fe.paymethod}` : ""}
                </FormLabel>
                <RadioGroup
                  row
                  value={form.paymethod}
                  onChange={(e) =>
                    p.onChange({
                      ...form,
                      paymethod: e.target.value as RechargeMethod,
                      payreference: "",
                    })
                  }
                >
                  <FormControlLabel
                    value="cash"
                    control={<Radio size="small" />}
                    label="Cash"
                  />
                  <FormControlLabel
                    value="upi"
                    control={<Radio size="small" />}
                    label="UPI"
                  />
                  <FormControlLabel
                    value="online"
                    disabled
                    control={<Radio size="small" />}
                    label="Pay online"
                  />
                </RadioGroup>
                <PayOnlineNotice
                  options={p.payOptions}
                  readFailed={p.payOptionsFailed}
                  why={
                    p.payOnlineOn
                      ? "Create the user first, then use Recharge › Pay online."
                      : PAY_ONLINE_OFF_NOTICE
                  }
                />
                {form.paymethod === "upi" && (
                  <UpiPayQR
                    options={p.payOptions}
                    amount={quoteTotal}
                    note="Internet service"
                  />
                )}
                {form.paymethod === "upi" && (
                  <TextField
                    size="small"
                    required
                    label="UPI reference (UTR)"
                    value={form.payreference}
                    onChange={(e) => set("payreference", e.target.value)}
                    {...err("payreference")}
                  />
                )}
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Record the full total {rupees(quoteTotal ?? 0)}; a part
                  payment goes through Billing › Record payment as an advance.
                </Typography>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                required={wired}
                label="ONU MAC / id"
                value={form.deviceid}
                onChange={(e) => set("deviceid", e.target.value)}
                helperText={
                  wired
                    ? "The operator's ONU signs in with this MAC; asked once, same plan and expiry"
                    : "Optional — the device signs in with this id; same plan and expiry, no second pin"
                }
                {...err("deviceid")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="ID proof type"
                value={form.idprooftype}
                onChange={(e) => set("idprooftype", e.target.value)}
                helperText="Optional"
                {...err("idprooftype")}
              >
                <MenuItem value="">None</MenuItem>
                {ID_PROOF_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="ID proof number"
                value={form.idproofnumber}
                onChange={(e) => set("idproofnumber", e.target.value)}
                helperText="Optional"
                {...err("idproofnumber")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                helperText="Optional. The subscriber confirms it, then gets their login details there."
                {...err("email")}
              />
            </Grid>
          </Grid>
        </Paper>

        {wired && (
          <Paper
            sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box>
              <Typography variant="subtitle1" component="h2">
                Billing address
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Required for a wired plan. Each login below is installed here
                unless you give its own address.
              </Typography>
            </Box>
            <AddressFields
              value={form.billing}
              onChange={(billing) => set("billing", billing)}
              disabled={p.pending}
              errors={fieldsUnder(fe, "billing.")}
            />
            <Typography variant="subtitle1" component="h2">
              Installation
            </Typography>
            {createLogins(form).map((l) => (
              <LoginInstallationFields
                key={l.role}
                username={l.username}
                role={l.role}
                deviceid={form.deviceid}
                value={form.installs[l.role]}
                onChange={(v) =>
                  set("installs", { ...form.installs, [l.role]: v })
                }
                disabled={p.pending}
                errors={fieldsUnder(fe, `install.${l.role}.`)}
              />
            ))}
            <ExtraLoginsNote count={cart.extraDevices ?? 0} />
          </Paper>
        )}

        {p.touched && problems.length > 0 && (
          <Alert severity="error">Fix the fields marked in red above.</Alert>
        )}

        {failure && (
          <Alert severity={failure.partial ? "warning" : "error"}>
            <AlertTitle>{failure.title}</AlertTitle>
            {failure.message}
            {failure.deviceMissing && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Retry with the same details is safe: it adds the device.
              </Typography>
            )}
            {failure.installationsMissing && p.onRetry && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={p.pending}
                  onClick={p.onRetry}
                >
                  {p.pending ? "Retrying…" : "Retry"}
                </Button>
              </Box>
            )}
          </Alert>
        )}

        <Box>
          <Button
            variant="contained"
            disabled={
              p.pending ||
              (p.touched && problems.length > 0) ||
              Object.keys(inUse).length > 0
            }
            onClick={p.onSubmit}
          >
            {p.pending ? "Creating…" : "Create subscriber"}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{ maxWidth: 900, display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box>
        <Typography variant="h5" component="h1">
          Create subscriber
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Created in <code>{p.ns || "no namespace"}</code>. The mobile is the
          login. Its password is generated and goes to the subscriber by email —
          nobody at the counter sees it.
        </Typography>
      </Box>
      {!p.allowed && (
        <Alert severity="warning">
          This login cannot create subscribers: it holds{" "}
          {p.roles.length ? p.roles.join(", ") : "no role"}. Creating a
          subscriber needs the Operator or superadmin role in this
          provider&apos;s namespace.
        </Alert>
      )}
      {body}
    </Box>
  );
}

/** "14 Nov 2026, 23:59" in the viewer's zone; the raw value when unreadable. */
export function expiryLabel(rfc3339: string): string {
  const t = Date.parse(rfc3339);
  if (!rfc3339 || Number.isNaN(t)) return rfc3339 || "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(t);
}

function CreatedCard({
  created,
  email,
  total,
  onReset,
}: {
  created: CreatedSubscriber;
  email: string;
  total: number | null;
  onReset: () => void;
}) {
  const deviceOnly = isDeviceOnly(created);
  const installsOnly = isInstallationsOnly(created);
  const done = created.installations_done ?? [];
  const row = (label: string, value: ReactNode) => (
    <Box sx={{ display: "flex", gap: 2, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{value}</Box>
    </Box>
  );
  return (
    <Paper sx={{ p: 2 }}>
      {installsOnly ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Installations saved</AlertTitle>
          {installationsCompletedMessage(created)}
        </Alert>
      ) : deviceOnly ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Device added</AlertTitle>
          Device {created.deviceid} added to {created.mobile}. The mobile&apos;s
          password is unchanged.
        </Alert>
      ) : (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Subscriber created</AlertTitle>
            {created.name}
          </Alert>
          {row("Login", <code>{created.mobile}</code>)}
          {row(
            "Login details",
            <span>{loginDetailsLine(created.login_details, email)}</span>,
          )}
          {created.deviceid &&
            row("Device / ONU id", <code>{created.deviceid}</code>)}
        </>
      )}
      {!installsOnly &&
        done.length > 0 &&
        row("Installations saved", <span>{done.join(", ")}</span>)}
      {row("Plan", created.plan)}
      {row("Expiry", expiryLabel(created.expiry))}
      {total != null &&
        row(
          "Total recorded",
          <span>{total > 0 ? rupees(total) : "₹0 — no receipt"}</span>,
        )}
      {saleReceipts(created).map((rc) =>
        row(
          receiptKindLabel(rc.kind),
          <span>
            <code>{rc.receiptno}</code>
            {rc.amount != null ? ` · ${rupees(rc.amount)}` : ""}
          </span>,
        ),
      )}
      <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
        <Button component={RouterLink} to={RECHARGE_PATH}>
          Recharge this subscriber
        </Button>
        <Button variant="contained" onClick={onReset}>
          Create another
        </Button>
      </Box>
    </Paper>
  );
}

/** The errors whose key starts with `prefix`, with the prefix taken off: "billing.pin" -> "pin". */
export function fieldsUnder(
  fe: Record<string, string>,
  prefix: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fe))
    if (k.startsWith(prefix)) out[k.slice(prefix.length)] = v;
  return out;
}
